import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Button,
  TextField,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  IconButton,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog as MuiDialog,
  DialogTitle,
  Select,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CallIcon from "@mui/icons-material/Call";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReplyIcon from "@mui/icons-material/Reply";
import PropType from "prop-types";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import Dialog from "../Dialog";
import { i18n } from "../../translate/i18n";
import { makeStyles } from 'tss-react/mui';
import ButtonWithSpinner from "../ButtonWithSpinner";
import { AuthContext } from "../../context/Auth/AuthContext";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { head } from "lodash";
import api from "../../services/api";
import SelectConection from "../SelectConection";
import TemplatePreview from "../TemplatePreview";
import { toast } from "../../services/toast";

const LANGUAGE_SHORT_LABELS = {
  es: "Español",
  es_MX: "Español (MX)",
  es_AR: "Español (AR)",
  es_ES: "Español (ES)",
  en: "Inglés",
  en_US: "Inglés (US)",
  en_GB: "Inglés (GB)",
  pt_BR: "Portugués (BR)",
  pt_PT: "Portugués (PT)"
};

const MessageSchema = Yup.object().shape({
  name: Yup.string().min(3).max(50).required("Required"),
  bodyText: Yup.string().min(3).max(900).required("Required"),
  category: Yup.string().required("Required"),
  language: Yup.string().required("Required"),
  headerFormat: Yup.string().oneOf(["NONE", "TEXT", "IMAGE", "VIDEO", "DOCUMENT"])
});

const useStyles = makeStyles()((theme) => ({
  root: {
    "& .MuiTextField-root": {
      width: "100%"
    }
  },
  sectionTitle: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    fontSize: 13
  },
  accordion: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "10px !important",
    marginBottom: theme.spacing(1.25),
    boxShadow: "none",
    background: theme.palette.background.paper,
    overflow: "hidden",
    "&:before": { display: "none" }
  },
  accordionSummary: {
    minHeight: 46,
    backgroundColor:
      theme.palette.mode === "light" ? "#f6f7f9" : "rgba(255,255,255,0.035)",
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
      gap: 10,
      margin: "10px 0"
    }
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary
  },
  accordionAviso: {
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.error.main,
    backgroundColor: "rgba(211, 47, 47, 0.12)",
    padding: "1px 8px",
    borderRadius: 10
  },
  accordionMeta: {
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.05)"
        : "rgba(255,255,255,0.08)",
    padding: "1px 8px",
    borderRadius: 10
  },
  accordionDetails: {
    paddingTop: theme.spacing(1.5)
  },
  sectionPaper: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(0.5)
  },
  variablesRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  variableIndex: {
    width: 40,
    marginRight: theme.spacing(1),
    textAlign: "center",
    fontWeight: 500,
    fontSize: 12
  },
  variableCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === "light" ? "#fafafa" : "rgba(255,255,255,0.03)",
  },
  variableCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  variableChip: {
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: 13,
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.06)"
        : "rgba(255,255,255,0.08)",
    padding: "2px 8px",
    borderRadius: 6,
  },
  variableHint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  buttonsRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  buttonField: {
    marginRight: theme.spacing(1)
  },
  previewWrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  },
  previewFrame: {
    marginTop: theme.spacing(1),
    borderRadius: 14,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    border:
      "1px solid " +
      (theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "#e2e2e2"),
    boxShadow: "0 2px 10px rgba(0,0,0,0.14)"
  },
  previewTopBar: {
    backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    fontWeight: 600,
    fontSize: 14,
    padding: "12px 16px"
  },
  previewWallpaper: {
    flex: 1,
    minHeight: 200,
    backgroundColor: theme.palette.mode === "dark" ? "#0b141a" : "#efeae2",
    backgroundImage:
      theme.palette.mode === "dark"
        ? "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.035) 1px, transparent 1px)",
    backgroundSize: "18px 18px",
    padding: theme.spacing(2),
    overflowY: "auto"
  },
  previewBubble: {
    position: "relative",
    backgroundColor: theme.palette.mode === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
    borderRadius: 8,
    borderTopLeftRadius: 0,
    maxWidth: "88%",
    fontSize: 13.5,
    lineHeight: 1.42,
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    wordBreak: "break-word",
    overflow: "hidden"
  },
  previewBubbleContent: {
    padding: "7px 9px 4px 9px"
  },
  previewMedia: {
    height: 130,
    borderRadius: 6,
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#54656f",
    backgroundColor:
      theme.palette.mode === "dark" ? "#111b21" : "#e9edef",
    objectFit: "cover",
    width: "100%"
  },
  bubbleHeader: {
    fontWeight: 700,
    marginBottom: 4,
    whiteSpace: "pre-wrap"
  },
  bubbleBody: {
    whiteSpace: "pre-wrap"
  },
  bubbleFooter: {
    marginTop: 6,
    fontSize: 12,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#667781",
    whiteSpace: "pre-wrap"
  },
  bubbleTime: {
    textAlign: "right",
    fontSize: 11,
    color: theme.palette.mode === "dark" ? "#8696a0" : "#667781",
    marginTop: 2,
    lineHeight: 1
  },
  previewButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    color: "#00a5f4",
    fontSize: 13.5,
    fontWeight: 500,
    padding: "9px 8px",
    borderTop:
      "1px solid " +
      (theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "#e9edef"),
    "& svg": { fontSize: 17 }
  }
}));

const initialMessage = {
  id: null,
  name: "",
  category: "UTILITY",
  subCategory: "",
  language: "es",
  bodyText: "",
  footerText: "",
  headerFormat: "NONE",
  headerText: "",
  sampleMediaType: "NONE",
  sampleMediaUrl: "",
  visibleToAgent: true,
  forCampaign: false
};

function TemplateMessageDialog({
  modalOpen,
  saveMessage,
  editMessage,
  onClose,
  messageSelected,
  refresh
}) {
  const { classes } = useStyles();
  const { user } = useContext(AuthContext);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState(initialMessage);
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [whatsappId, setWhatsappId] = useState(null);
  const [additionalWhatsappIds, setAdditionalWhatsappIds] = useState([]);
  const [eligibleConnections, setEligibleConnections] = useState([]);

  useEffect(() => {
    if (!whatsappId) {
      setEligibleConnections([]);
      return;
    }

    (async () => {
      try {
        const { data } = await api.get("/template-message/eligible-connections", {
          params: { whatsappId }
        });
        setEligibleConnections(data || []);
        const validIds = (data || []).map((c) => c.id);
        setAdditionalWhatsappIds((prev) => prev.filter((id) => validIds.includes(id)));
      } catch {
        setEligibleConnections([]);
      }
    })();
  }, [whatsappId]);

  const [variables, setVariables] = useState([]);
  const [buttons, setButtons] = useState([]);

  useEffect(() => {
    setDialogOpen(modalOpen);
    verifyAndSetMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, messageSelected]);

  const messageSelectedIsValid = () =>
    messageSelected && typeof messageSelected === "object" && messageSelected.id;

  const comprobarNombre = (nombre, whatsappIdActual) => {
    clearTimeout(comprobacionNombre.current);

    if (!nombre || String(nombre).trim().length < 3) {
      setAvisoNombre(null);
      return;
    }

    comprobacionNombre.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/template-message/check-name", {
          params: {
            name: nombre,
            whatsappId: whatsappIdActual || "",
            ...(messageSelected?.id ? { id: messageSelected.id } : {})
          }
        });
        setAvisoNombre(data);
      } catch {
        setAvisoNombre(null);
      }
    }, 450);
  };

  const metaEditLock = useMemo(() => {
    const esEdicion =
      messageSelected &&
      typeof messageSelected === "object" &&
      messageSelected.id;

    if (!esEdicion || !messageSelected.metaTemplateId) return null;

    if (String(messageSelected.status || "").toUpperCase() === "PENDING") {
      return {
        texto:
          "Meta está revisando esta plantilla y no acepta ediciones hasta que responda. Si guardas ahora, los cambios quedan solo en el sistema y NO llegan a WhatsApp.",
      };
    }

    const ultima = messageSelected.lastMetaSubmitAt
      ? new Date(messageSelected.lastMetaSubmitAt)
      : null;

    if (ultima && !Number.isNaN(ultima.getTime())) {
      const liberaEn = new Date(ultima.getTime() + 24 * 60 * 60 * 1000);
      if (liberaEn > new Date()) {
        return {
          texto: `Meta solo permite editar una plantilla activa una vez cada 24 horas. Podrás publicar cambios a partir del ${liberaEn.toLocaleString()}. Si guardas antes, quedan solo en el sistema y NO llegan a WhatsApp.`,
        };
      }
    }

    if (messageSelected.statusReason) {
      return {
        texto: `Última respuesta de Meta sobre esta plantilla: "${messageSelected.statusReason}"`,
      };
    }

    return null;
  }, [messageSelected]);

  const verifyAndSetMessage = () => {
    if (messageSelectedIsValid()) {
      setMessage({
        id: messageSelected.id,
        name: messageSelected.name || "",
        category: messageSelected.category || "UTILITY",
        subCategory: messageSelected.subCategory || "",
        language: messageSelected.language || "es",
        bodyText: messageSelected.bodyText || "",
        footerText: messageSelected.footerText || "",
        headerFormat: messageSelected.headerFormat || "NONE",
        headerText: messageSelected.headerText || "",
        sampleMediaType: messageSelected.sampleMediaType || "NONE",
        sampleMediaUrl: messageSelected.sampleMediaUrl || "",
        visibleToAgent: messageSelected.visibleToAgent !== false,
        forCampaign: messageSelected.forCampaign === true
      });

      setVariables(
        (messageSelected.variables || [])
          .map((v, idx) => ({
            position: v.position || idx + 1,
            exampleValue: v.exampleValue || "",
            valueType: v.valueType || "TEXT",
            label: v.label || "",
            source: v.source || "MANUAL"
          }))
          .sort((a, b) => a.position - b.position)
      );

      setButtons(
        (messageSelected.buttons || [])
          .map((b, idx) => ({
            position: b.position || idx + 1,
            type: b.type || "QUICK_REPLY",
            text: b.text || "",
            url: b.url || "",
            phoneNumber: b.phoneNumber || "",
            payload: b.payload || ""
          }))
          .sort((a, b) => a.position - b.position)
      );

      setWhatsappId(Number(messageSelected.whatsappId) || null);
      setAdditionalWhatsappIds(
        (messageSelected.whatsapps || []).map((w) => Number(w.id))
      );
    } else {
      setMessage(initialMessage);
      setVariables([]);
      setButtons([]);
      setWhatsappId(null);
      setAdditionalWhatsappIds([]);
    }
  };

  const handleClose = () => {
    setAttachment(null);
    setLoading(false);
    setAbiertoIzq("basica");
    setAbiertoDer("conexion");
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const handleAddVariable = (values, setFieldValue) => {
    const nextPosition = variables.length + 1;
    const placeholder = `{{${nextPosition}}}`;

    setVariables([
      ...variables,
      {
        position: nextPosition,
        exampleValue: "",
        valueType: "TEXT",
        label: "",
        source: "MANUAL"
      }
    ]);

    const currentBody = values.bodyText || "";
    const newBody = (currentBody + " " + placeholder).trim();
    setFieldValue("bodyText", newBody);
  };

  const handleVariableExampleChange = (index, value) => {
    const next = [...variables];
    next[index].exampleValue = value;
    setVariables(next);
  };

  const handleVariableLabelChange = (index, value) => {
    const next = [...variables];
    next[index].label = value;
    setVariables(next);
  };

  const handleVariableSourceChange = (index, value) => {
    const next = [...variables];
    next[index].source = value;
    setVariables(next);
  };

  const handleAddButton = () => {
    const nextPosition = buttons.length + 1;
    setButtons([
      ...buttons,
      {
        position: nextPosition,
        type: "QUICK_REPLY",
        text: "",
        url: "",
        phoneNumber: "",
        payload: ""
      }
    ]);
  };

  const handleButtonChange = (index, field, value) => {
    const next = [...buttons];
    next[index][field] = value;
    setButtons(next);
  };

  const handleMoveButton = (index, delta) => {
    const destino = index + delta;
    if (destino < 0 || destino >= buttons.length) return;

    const next = [...buttons];
    [next[index], next[destino]] = [next[destino], next[index]];
    setButtons(next.map((b, idx) => ({ ...b, position: idx + 1 })));
  };

  const errorBotones = useMemo(() => {
    if (buttons.length === 0) return null;

    const tipos = buttons.map((b) =>
      b.type === "QUICK_REPLY" ? "Q" : "C"
    );
    const grupos = tipos.join("").replace(/Q+/g, "Q").replace(/C+/g, "C");

    if (grupos.length > 2) {
      return "Las respuestas rápidas deben ir juntas. Mueve los botones para agruparlas.";
    }

    const quick = tipos.filter((t) => t === "Q").length;
    if (quick > 3) return `Meta permite máximo 3 respuestas rápidas y tienes ${quick}.`;

    return null;
  }, [buttons]);

  const handleRemoveButton = (index) => {
    const next = [...buttons];
    next.splice(index, 1);
    setButtons(
      next.map((b, idx) => ({
        ...b,
        position: idx + 1
      }))
    );
  };

  const [confirmacion, setConfirmacion] = useState(null);

  const accionGuardadoRef = useRef("local");

  const [avisoNombre, setAvisoNombre] = useState(null);
  const comprobacionNombre = useRef(null);

  const [abiertoIzq, setAbiertoIzq] = useState("basica");
  const [abiertoDer, setAbiertoDer] = useState("conexion");

  const seccionIzq = (clave) => ({
    expanded: abiertoIzq === clave,
    onChange: (e, abre) => setAbiertoIzq(abre ? clave : "")
  });
  const seccionDer = (clave) => ({
    expanded: abiertoDer === clave,
    onChange: (e, abre) => setAbiertoDer(abre ? clave : "")
  });

  const handleSave = async (values) => {
    if (errorBotones) {
      toast.error(errorBotones);
      return;
    }

    if (accionGuardadoRef.current === "local") {
      await guardar(values, false);
      return;
    }

    const yaEstaEnMeta =
      messageSelectedIsValid() && messageSelected.metaTemplateId;

    if (yaEstaEnMeta) {
      setConfirmacion(values);
      return;
    }

    await guardar(values, true);
  };

  const guardar = async (values, submitToMeta) => {
    try {
      setLoading(true);

      const payload = {
        name: values.name,
        category: values.category,
        subCategory: values.subCategory || "",
        language: values.language,
        bodyText: values.bodyText,
        footerText: values.footerText || "",
        headerFormat: values.headerFormat,
        headerText: values.headerText || "",
        sampleMediaType: values.sampleMediaType || "NONE",
        sampleMediaUrl: values.sampleMediaUrl || "",
        variableType: "TEXT",
        variables: variables.map((v, idx) => ({
          position: idx + 1,
          exampleValue: v.exampleValue,
          valueType: v.valueType || "TEXT",
          label: v.label || "",
          source: v.source || "MANUAL"
        })),
        buttons: buttons.map((b, idx) => ({
          position: idx + 1,
          type: b.type,
          text: b.text,
          url: b.url || "",
          phoneNumber: b.phoneNumber || "",
          payload: b.payload || ""
        })),
        whatsappId: whatsappId || "",
        additionalWhatsappIds,
        visibleToAgent: values.visibleToAgent !== false,
        forCampaign: values.forCampaign === true,
        userId: user.id,
        pendingMediaUpload: !!attachment,
        ...(submitToMeta !== undefined && { submitToMeta })
      };

      const formData = new FormData();
      if (attachment) {
        formData.append("file", attachment);
      }

      let metaWarning = null;

      if (messageSelectedIsValid()) {
        await editMessage({
          ...messageSelected,
          ...payload
        });

        if (attachment && messageSelected.id) {
          const { data } = await api.post(
            `/template-message/${messageSelected.id}/media-upload`,
            formData
          );
          metaWarning = data?.metaWarning || null;
        }
        toast.success("Plantilla actualizada con éxito.");
      } else {
        const { data } = await api.post("/template-message", payload);
        metaWarning = data?.metaWarning || null;

        if (attachment && data && data.id) {
          const { data: uploaded } = await api.post(
            `/template-message/${data.id}/media-upload`,
            formData
          );
          metaWarning = uploaded?.metaWarning || null;
        }
        toast.success("Plantilla creada con éxito.");
        if (refresh) await refresh();
      }

      if (metaWarning) {
        toast.warn(metaWarning, { duration: 10000 });
      }

      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(
        (error && error.message) || "Error al guardar la plantilla."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = (values) => (
    <TemplatePreview
      template={values}
      variables={variables}
      buttons={buttons}
    />
  );

  return (
    <>
    <Dialog
      title="Plantillas de Mensajes (WhatsApp Cloud)"
      modalOpen={dialogOpen}
      onClose={handleClose}
      disableBackdropClose
      fullWidth
      maxWidth="lg"
    >
      <Formik
        initialValues={message}
        enableReinitialize
        onSubmit={handleSave}
        validationSchema={MessageSchema}
      >
        {({ touched, errors, values, setFieldValue, submitCount }) => (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden"
            }}
          >
            <DialogContent className={classes.root} dividers>
              {metaEditLock && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    mb: 2,
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    borderColor: "#ed6c02",
                    backgroundColor: "rgba(237, 108, 2, 0.08)"
                  }}
                >
                  <Typography sx={{ fontSize: 18, lineHeight: 1.2 }}>⚠️</Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {metaEditLock.texto}
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                {}
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  {}
                  <Accordion {...seccionIzq("basica")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Información básica</Typography>
                      {submitCount > 0 &&
                        Boolean(errors.name || errors.category || errors.language) && (
                          <span className={classes.accordionAviso}>Falta completar</span>
                        )}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={1}>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6
                        }}>
                        <Field
                          as={TextField}
                          name="name"
                          label="Nombre"
                          variant="outlined"
                          size="small"
                          onChange={(e) => {
                            setFieldValue("name", e.target.value);
                            comprobarNombre(e.target.value, whatsappId);
                          }}
                          error={
                            (touched.name && Boolean(errors.name)) ||
                            avisoNombre?.libre === false
                          }
                          helperText={
                            (touched.name && errors.name) ||
                            (avisoNombre?.libre === false
                              ? "Ese nombre ya está usado en esta conexión"
                              : avisoNombre?.nombre &&
                                avisoNombre.nombre !== values.name
                              ? `Se guardará como "${avisoNombre.nombre}"`
                              : " ")
                          }
                        />

                        {avisoNombre?.libre === false && avisoNombre?.sugerido && (
                          <Button
                            size="small"
                            sx={{ mt: 0.5, textTransform: "none" }}
                            onClick={() => {
                              setFieldValue("name", avisoNombre.sugerido);
                              setAvisoNombre(null);
                            }}
                          >
                            Usar "{avisoNombre.sugerido}"
                          </Button>
                        )}
                      </Grid>
                      <Grid
                        size={{
                          xs: 6,
                          sm: 3
                        }}>
                        <Field
                          as={TextField}
                          select
                          name="category"
                          label="Categoría"
                          variant="outlined"
                          size="small"
                          error={touched.category && Boolean(errors.category)}
                          helperText={touched.category && errors.category}
                        >
                          <MenuItem value="UTILITY">Utilidad</MenuItem>
                          <MenuItem value="MARKETING">Marketing</MenuItem>
                          <MenuItem value="AUTHENTICATION">
                            Autenticación
                          </MenuItem>
                        </Field>
                      </Grid>
                      <Grid
                        size={{
                          xs: 6,
                          sm: 3
                        }}>
                        <Field
                          as={TextField}
                          select
                          name="language"
                          label="Idioma"
                          variant="outlined"
                          size="small"
                          error={touched.language && Boolean(errors.language)}
                          helperText={touched.language && errors.language}
                          slotProps={{
                            select: {
                              renderValue: (val) => LANGUAGE_SHORT_LABELS[val] || val
                            }
                          }}
                        >
                          {}
                          <MenuItem value="es">Español (Latinoamérica / Colombia)</MenuItem>
                          <MenuItem value="es_MX">Español (México)</MenuItem>
                          <MenuItem value="es_AR">Español (Argentina)</MenuItem>
                          <MenuItem value="es_ES">Español (España)</MenuItem>
                          <MenuItem value="en">Inglés</MenuItem>
                          <MenuItem value="en_US">Inglés (EE.UU.)</MenuItem>
                          <MenuItem value="en_GB">Inglés (Reino Unido)</MenuItem>
                          <MenuItem value="pt_BR">Portugués (Brasil)</MenuItem>
                          <MenuItem value="pt_PT">Portugués (Portugal)</MenuItem>
                        </Field>
                      </Grid>
                    </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionIzq("encabezado")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Encabezado</Typography>
                      <span className={classes.accordionMeta}>
                        {values.headerFormat === "NONE" || !values.headerFormat
                          ? "Ninguno"
                          : values.headerFormat === "TEXT"
                          ? "Texto"
                          : values.headerFormat === "IMAGE"
                          ? "Imagen"
                          : values.headerFormat === "VIDEO"
                          ? "Video"
                          : "Documento"}
                      </span>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={1}>
                      {}
                      <Grid size={12}>
                        <Field
                          as={TextField}
                          select
                          name="headerFormat"
                          label="Tipo de header"
                          variant="outlined"
                          size="small"
                        >
                          <MenuItem value="NONE">Ninguno</MenuItem>
                          <MenuItem value="TEXT">Texto</MenuItem>
                          <MenuItem value="IMAGE">Imagen</MenuItem>
                          <MenuItem value="VIDEO">Video</MenuItem>
                          <MenuItem value="DOCUMENT">Documento</MenuItem>
                        </Field>
                      </Grid>

                      {}
                      {values.headerFormat === "TEXT" && (
                        <Grid size={12}>
                          <Field
                            as={TextField}
                            name="headerText"
                            label="Texto del header"
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      )}

                      {}
                      {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                        values.headerFormat
                      ) && (
                        <Grid size={12}>
                          <Typography variant="body2" gutterBottom>
                            Subir archivo de ejemplo (media de header)
                          </Typography>
                          <IconButton
                            component="label"
                            color="primary"
                            size="small"
                          >
                            <AttachFileIcon fontSize="small" />
                            <input
                              type="file"
                              hidden
                              ref={attachmentFile}
                              onChange={handleAttachmentFile}
                            />
                          </IconButton>
                          {attachment && (
                            <Typography variant="caption">
                              {attachment.name}
                            </Typography>
                          )}
                          {!attachment && values.sampleMediaUrl && (
                            <Typography variant="caption" color="textSecondary">
                              Ya hay un archivo cargado. Adjunta otro solo si
                              quieres reemplazarlo.
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionIzq("cuerpo")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Cuerpo del mensaje</Typography>
                      {submitCount > 0 && Boolean(errors.bodyText) && (
                        <span className={classes.accordionAviso}>Falta completar</span>
                      )}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={1}>
                      <Grid size={12}>
                        <Field
                          as={TextField}
                          name="bodyText"
                          label="Mensaje"
                          multiline
                          rows={6}
                          variant="outlined"
                          fullWidth
                          size="small"
                          error={
                            touched.bodyText && Boolean(errors.bodyText)
                          }
                          helperText={touched.bodyText && errors.bodyText}
                        />
                      </Grid>
                      <Grid size={12}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handleAddVariable(values, setFieldValue)
                          }
                        >
                          {i18n.t("templateMessage.dialog.addVariable") ||
                            "Agregar variable {{n}}"}
                        </Button>
                      </Grid>
                    </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionIzq("variables")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Variables (ejemplos)</Typography>
                      {variables.length > 0 && (
                        <span className={classes.accordionMeta}>{variables.length}</span>
                      )}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    {variables.length === 0 && (
                      <Typography variant="body2">
                        No hay variables definidas. Agrega variables desde el
                        cuerpo del mensaje usando el botón "Agregar variable".
                      </Typography>
                    )}
                    {variables.map((v, index) => {
                      const source = v.source || "MANUAL";
                      const isManual = source === "MANUAL";
                      return (
                        <div key={index} className={classes.variableCard}>
                          <div className={classes.variableCardHeader}>
                            <span className={classes.variableChip}>{`{{${v.position}}}`}</span>
                            <span className={classes.variableHint}>
                              {isManual
                                ? "Lo escribe el agente al enviar"
                                : source === "CONTACT_NAME"
                                ? "Se llena solo con el nombre del contacto"
                                : "Se llena solo con el nombre del usuario"}
                            </span>
                          </div>

                          <TextField
                            select
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Origen del dato"
                            value={source}
                            onChange={(e) =>
                              handleVariableSourceChange(index, e.target.value)
                            }
                          >
                            <MenuItem value="MANUAL">
                              Manual (lo escribe el agente)
                            </MenuItem>
                            <MenuItem value="CONTACT_NAME">
                              Automático: nombre del contacto
                            </MenuItem>
                            <MenuItem value="USER_NAME">
                              Automático: nombre del usuario
                            </MenuItem>
                          </TextField>

                          {isManual && (
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              label="¿Qué información va aquí?"
                              placeholder="ej: número de orden"
                              value={v.label || ""}
                              onChange={(e) =>
                                handleVariableLabelChange(index, e.target.value)
                              }
                            />
                          )}

                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={`Ejemplo para {{${v.position}}}`}
                            value={v.exampleValue}
                            onChange={(e) =>
                              handleVariableExampleChange(index, e.target.value)
                            }
                          />
                        </div>
                      );
                    })}
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionIzq("pie")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Pie de mensaje</Typography>
                      {values.footerText ? (
                        <span className={classes.accordionMeta}>1</span>
                      ) : null}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <Grid container spacing={1}>
                      <Grid size={12}>
                        <Field
                          as={TextField}
                          name="footerText"
                          label="Texto del pie"
                          multiline
                          rows={2}
                          variant="outlined"
                          fullWidth
                          size="small"
                        />
                      </Grid>
                    </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionIzq("botones")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Botones</Typography>
                      {buttons.length > 0 && (
                        <span className={classes.accordionMeta}>{buttons.length}</span>
                      )}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    {buttons.map((btn, index) => (
                      <div key={index} className={classes.buttonsRow}>
                        <TextField
                          className={classes.buttonField}
                          select
                          variant="outlined"
                          size="small"
                          label="Tipo"
                          value={btn.type}
                          onChange={(e) =>
                            handleButtonChange(index, "type", e.target.value)
                          }
                        >
                          <MenuItem value="QUICK_REPLY">
                            Respuesta rápida
                          </MenuItem>
                          <MenuItem value="URL">URL</MenuItem>
                          <MenuItem value="PHONE_NUMBER">Llamada</MenuItem>
                          <MenuItem value="FLOW">Flujo (Flow)</MenuItem>
                        </TextField>

                        <TextField
                          className={classes.buttonField}
                          variant="outlined"
                          size="small"
                          label="Texto"
                          value={btn.text}
                          onChange={(e) =>
                            handleButtonChange(index, "text", e.target.value)
                          }
                        />

                        {btn.type === "URL" && (
                          <TextField
                            className={classes.buttonField}
                            variant="outlined"
                            size="small"
                            label="URL"
                            value={btn.url}
                            onChange={(e) =>
                              handleButtonChange(
                                index,
                                "url",
                                e.target.value
                              )
                            }
                          />
                        )}

                        {btn.type === "PHONE_NUMBER" && (
                          <TextField
                            className={classes.buttonField}
                            variant="outlined"
                            size="small"
                            label="Teléfono"
                            value={btn.phoneNumber}
                            onChange={(e) =>
                              handleButtonChange(
                                index,
                                "phoneNumber",
                                e.target.value
                              )
                            }
                          />
                        )}

                        {}
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => handleMoveButton(index, -1)}
                          title="Subir"
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          disabled={index === buttons.length - 1}
                          onClick={() => handleMoveButton(index, 1)}
                          title="Bajar"
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => handleRemoveButton(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddButton}
                    >
                      Agregar botón
                    </Button>

                    {}
                    {errorBotones && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 1,
                          color: "warning.main",
                          fontWeight: 600,
                        }}
                      >
                        {errorBotones}
                      </Typography>
                    )}
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {}
                <Grid
                  size={{
                    xs: 12,
                    md: 6
                  }}
                  sx={{ display: "flex", flexDirection: "column" }}>
                  {}
                  <Accordion {...seccionDer("conexion")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Conexión WhatsApp Cloud</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <SelectConection
                      value={whatsappId}
                      onChange={setWhatsappId}
                      channel="whatsapp_cloud"
                    />

                    {whatsappId && (
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel id="additional-connections-label">
                          Conexiones adicionales
                        </InputLabel>
                        <Select
                          labelId="additional-connections-label"
                          multiple
                          value={additionalWhatsappIds}
                          onChange={(e) => setAdditionalWhatsappIds(e.target.value)}
                          input={<OutlinedInput label="Conexiones adicionales" />}
                          renderValue={(selected) => (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {selected.map((id) => {
                                const conn = eligibleConnections.find((c) => c.id === id);
                                return <Chip key={id} label={conn ? conn.name : id} size="small" />;
                              })}
                            </div>
                          )}
                        >
                          {eligibleConnections.length === 0 && (
                            <MenuItem disabled>
                              No hay otras conexiones con la misma cuenta de Meta
                            </MenuItem>
                          )}
                          {eligibleConnections.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography className={classes.accordionMeta} style={{ marginTop: 4 }}>
                          Solo conexiones de la MISMA cuenta de Meta: ahí es donde Meta ya tiene esta plantilla aprobada.
                        </Typography>
                      </FormControl>
                    )}
                    </AccordionDetails>
                  </Accordion>

                  {}
                  <Accordion {...seccionDer("disponibilidad")} disableGutters className={classes.accordion}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                      <Typography className={classes.accordionTitle}>Disponibilidad</Typography>
                      <span className={classes.accordionMeta}>
                        {values.visibleToAgent !== false ? "Para agentes" : "Uso interno"}
                      </span>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.visibleToAgent !== false}
                          onChange={(e) =>
                            setFieldValue("visibleToAgent", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        values.visibleToAgent !== false
                          ? "Disponible para los agentes en el ticket"
                          : "Uso interno del sistema (oculta en el ticket)"
                      }
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: "block", marginTop: 4 }}
                    >
                      Si la apagas, esta plantilla NO aparecerá en el selector del
                      chat; se usará solo internamente (bienvenida, automatizaciones,
                      etc.).
                    </Typography>

                    <FormControlLabel
                      style={{ marginTop: 12 }}
                      control={
                        <Switch
                          checked={values.forCampaign === true}
                          onChange={(e) =>
                            setFieldValue("forCampaign", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        values.forCampaign === true
                          ? "Disponible para campañas masivas"
                          : "No se ofrece en campañas"
                      }
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: "block", marginTop: 4 }}
                    >
                      Solo las marcadas aquí aparecen al armar una campaña. Sirve
                      para no mezclar las de promoción con las transaccionales
                      (avisos de entrega, confirmaciones).
                    </Typography>

                    {values.forCampaign === true &&
                      variables.some(
                        (v) => String(v.source || "").toUpperCase() === "USER_NAME"
                      ) && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          style={{ display: "block", marginTop: 8 }}
                        >
                          Esta plantilla usa <b>nombre del usuario</b>. En una
                          campaña no hay un agente que la resuelva, así que
                          habrá que escribir ese valor a mano y será el mismo
                          para todos los destinatarios. Si querías el nombre de
                          cada cliente, usa <b>nombre del contacto</b>.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {renderPreview(values)}
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} color="primary" size="small">
                Cancelar
              </Button>
              <ButtonWithSpinner
                loading={loading}
                type="submit"
                variant="outlined"
                size="small"
                onClick={() => { accionGuardadoRef.current = "local"; }}
              >
                Guardar en local
              </ButtonWithSpinner>
              <ButtonWithSpinner
                loading={loading}
                color="primary"
                type="submit"
                variant="contained"
                size="small"
                onClick={() => { accionGuardadoRef.current = "meta"; }}
              >
                Enviar a Meta
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>

      {}
      <MuiDialog
        open={!!confirmacion}
        onClose={() => setConfirmacion(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>¿Publicar los cambios en Meta?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Esta plantilla ya está registrada en Meta. Si publicas los cambios:
          </Typography>
          <ul style={{ margin: "8px 0 12px 18px", padding: 0, fontSize: 14 }}>
            <li>
              <b>Vuelve a revisión</b> y deja de estar aprobada hasta que Meta
              responda. Mientras tanto no se puede enviar.
            </li>
            <li>
              <b>Gasta la edición del día.</b> Meta solo permite editar una
              plantilla activa una vez cada 24 horas.
            </li>
          </ul>
          <Typography variant="body2" color="textSecondary">
            Si no era tu intención publicar, cancela y la plantilla queda como
            está.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmacion(null)} size="small">
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => {
              const values = confirmacion;
              setConfirmacion(null);
              guardar(values, true);
            }}
          >
            Continuar
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}

TemplateMessageDialog.propTypes = {
  modalOpen: PropType.bool,
  saveMessage: PropType.func,
  editMessage: PropType.func,
  onClose: PropType.func,
  messageSelected: PropType.object,
  refresh: PropType.func
};

export default TemplateMessageDialog;
